import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { OnlineUser } from "hooks/useCommandRelay";
import {
  ClipboardDocumentIcon,
  CheckIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";

// Storage keys for cross-tab sync
const STORAGE_KEY_TOASTS = "command_relay_toasts";
const STORAGE_KEY_DISMISS = "command_relay_dismiss_event";

export interface CommandToastData {
  id: string;
  fromUser: OnlineUser;
  label: string;
  copyValue: string;
  navigateTo: string | null;
  timestamp: number;
}

// Get toasts from localStorage
function getStoredToasts(): CommandToastData[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_TOASTS);
    if (!stored) return [];
    const toasts: CommandToastData[] = JSON.parse(stored);
    // Filter out toasts older than 60 seconds
    const now = Date.now();
    return toasts.filter(t => now - t.timestamp < 60000);
  } catch {
    return [];
  }
}

// Save toasts to localStorage
function saveToasts(toasts: CommandToastData[]) {
  localStorage.setItem(STORAGE_KEY_TOASTS, JSON.stringify(toasts));
}

// Add a toast (called from provider)
export function addCommandToast(toast: CommandToastData) {
  const toasts = getStoredToasts();
  // Check if already exists
  if (toasts.some(t => t.id === toast.id)) return;
  toasts.push(toast);
  saveToasts(toasts);
  // Trigger storage event for other tabs
  window.dispatchEvent(new Event("command_toast_update"));
}

// Remove a toast by ID
export function removeCommandToast(toastId: string) {
  const toasts = getStoredToasts();
  const filtered = toasts.filter(t => t.id !== toastId);
  saveToasts(filtered);
  // Trigger local update
  window.dispatchEvent(new Event("command_toast_update"));
  // Broadcast dismiss to other tabs via storage event
  localStorage.setItem(STORAGE_KEY_DISMISS, JSON.stringify({ id: toastId, ts: Date.now() }));
  setTimeout(() => localStorage.removeItem(STORAGE_KEY_DISMISS), 50);
}

// Single toast item component
function ToastItem({ toast, onDismiss }: { toast: CommandToastData; onDismiss: (id: string) => void }) {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(toast.copyValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss(toast.id);
    if (toast.navigateTo) {
      navigate(toast.navigateTo);
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss(toast.id);
  };

  return (
    <div className="flex items-center gap-2 min-w-[280px] p-3 bg-gradient-to-r from-slate-800 to-slate-700 border-l-4 border-indigo-500 rounded-lg shadow-lg animate-slide-in">
      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-400 truncate">
          {toast.fromUser.name} (@{toast.fromUser.username})
        </p>
        <p className="text-sm font-medium text-white truncate">{toast.label}</p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Copy Button */}
        <button
          onClick={handleCopy}
          className="p-1.5 text-gray-300 hover:text-white hover:bg-white/10 rounded transition-colors"
          title="Copy"
        >
          {copied ? (
            <CheckIcon className="w-4 h-4 text-green-400" />
          ) : (
            <ClipboardDocumentIcon className="w-4 h-4" />
          )}
        </button>

        {/* Open Button */}
        {toast.navigateTo && (
          <button
            onClick={handleOpen}
            className="p-1.5 text-white bg-green-600 hover:bg-green-500 rounded transition-colors"
            title="Open"
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          </button>
        )}

        {/* Dismiss Button */}
        <button
          onClick={handleDismiss}
          className="p-1.5 text-gray-300 hover:text-white hover:bg-red-500/20 rounded transition-colors"
          title="Dismiss"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Container that renders all toasts
export default function CommandToastContainer() {
  const [toasts, setToasts] = useState<CommandToastData[]>([]);

  // Load toasts and set up listeners
  useEffect(() => {
    // Initial load
    setToasts(getStoredToasts());

    // Listen for local updates
    const handleLocalUpdate = () => {
      setToasts(getStoredToasts());
    };

    // Listen for cross-tab storage events
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY_TOASTS) {
        setToasts(getStoredToasts());
      }
      if (e.key === STORAGE_KEY_DISMISS && e.newValue) {
        // Another tab dismissed a toast
        setToasts(getStoredToasts());
      }
    };

    window.addEventListener("command_toast_update", handleLocalUpdate);
    window.addEventListener("storage", handleStorageChange);

    // Auto-cleanup expired toasts every 5 seconds
    const interval = setInterval(() => {
      const current = getStoredToasts();
      if (current.length !== toasts.length) {
        setToasts(current);
        saveToasts(current);
      }
    }, 5000);

    return () => {
      window.removeEventListener("command_toast_update", handleLocalUpdate);
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const handleDismiss = useCallback((toastId: string) => {
    // Immediately update local state for instant feedback
    setToasts(prev => prev.filter(t => t.id !== toastId));
    // Then sync to localStorage and other tabs
    removeCommandToast(toastId);
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onDismiss={handleDismiss} />
      ))}
    </div>
  );
}
